import { Component, effect, Input, OnDestroy, OnInit, QueryList, signal, ViewChildren, WritableSignal } from '@angular/core';
import { MITIGATION_STATUSES, MITIGATION_STATUS_OPTIONS } from '@ids-constants';
import { BaseComponent } from '@ids-components';
import { ThreatService } from '@ids-services';
import { CommonToolbarConfiguration, CommonToolbarResult } from '@microsec/models';

import { finalize } from 'rxjs';
import { DELETE_LABEL, PER_PAGE, PER_PAGE_OPTIONS } from '@microsec/constants';
import { CardComponent, CommonToolbarComponent, LoadingOverlayComponent } from '@microsec/components';
import { MitigationFormComponent } from '../mitigation-form/mitigation-form.component';
import { ConstantPipe } from '@ids-pipes';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DataViewModule } from 'primeng/dataview';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

const FILTER_CONFIG: CommonToolbarConfiguration = {
  types: ['search'],
  hideResetSortOption: true,
  showTotalRecords: true,
  totalRecordsIcon: 'fa fa-shield-halved',
  searchPlaceholder: 'Search Recommendation Name, Description...',
};

@Component({
  selector: 'app-mitigation-recommendation',
  templateUrl: './mitigation-recommendation.component.html',
  styleUrls: ['./mitigation-recommendation.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TooltipModule,
    DataViewModule,
    SelectModule,
    CardComponent,
    LoadingOverlayComponent,
    CommonToolbarComponent,
    ConstantPipe,
  ],
  providers: [ConstantPipe],
})
export class MitigationRecommendationComponent extends BaseComponent implements OnInit, OnDestroy {
  isLoading = false;

  /* Set Threat Details */

  _threat = null;

  get threat() {
    return this._threat;
  }

  @Input() set threat(value: any) {
    this._threat = value;
    if (!!this.threat?.threat_id) {
      this.getThreatMitigation();
    }
  }

  _threatGroup: any = null;

  get threatGroup() {
    return this._threatGroup;
  }

  @Input() set threatGroup(value: any) {
    this._threatGroup = value;
    if (this.threatGroup) {
      const mitigations = Object.values(this.threatGroup.mitigations || {}) || [];
      this.setMitigations(mitigations);
    }
  }

  /* Mitigation Data */

  allMitigations: any[] = [];

  filteredActedMitigations: any[] = [];

  filteredUnactedMitigations: any[] = [];

  /* FilterObject */

  filterActedConfiguration: CommonToolbarConfiguration = this.util.cloneDeepObject(FILTER_CONFIG);

  filterUnactedConfiguration: CommonToolbarConfiguration = this.util.cloneDeepObject(FILTER_CONFIG);

  filterActedObject: WritableSignal<CommonToolbarResult | null> = signal(null);

  filterUnactedObject: WritableSignal<CommonToolbarResult | null> = signal(null);

  statusOptions = MITIGATION_STATUS_OPTIONS.filter(
    (option) => option.value !== MITIGATION_STATUSES.ACTED && option.value !== MITIGATION_STATUSES.UNACTED,
  );

  MITIGATION_STATUSES = MITIGATION_STATUSES;

  /* Pagination */

  PER_PAGE = PER_PAGE;

  PER_PAGE_OPTIONS = PER_PAGE_OPTIONS;

  @ViewChildren('ct') cts!: QueryList<CommonToolbarComponent>;

  constructor(private threatSrv: ThreatService) {
    super();

    /**
     * Handle Filter Object subscription manually from Unacted and Acted Mitigations/Recommendations
     * FE filter search
     */
    effect(() => {
      const values = this.filterUnactedObject();
      if (!!values) {
        const filteredMitigations = [...this.allMitigations.filter((mitigation) => !mitigation.is_acted)];
        if (!!values.search) {
          this.filteredUnactedMitigations = filteredMitigations.filter(
            (m) => m.title.toLowerCase().includes(values.search?.toLowerCase()) || m.description.toLowerCase().includes(values.search?.toLowerCase()),
          );
        }
        if (values.search === '') {
          this.filteredUnactedMitigations = [...filteredMitigations];
        }
      }
      this.filterUnactedConfiguration.totalRecords = this.filteredUnactedMitigations.length;
    });
    effect(() => {
      const values = this.filterActedObject();
      if (!!values) {
        const filteredMitigations = [...this.allMitigations.filter((mitigation) => !!mitigation.is_acted)];
        if (!!values.search) {
          this.filteredActedMitigations = filteredMitigations.filter(
            (m) => m.title.toLowerCase().includes(values.search?.toLowerCase()) || m.description.toLowerCase().includes(values.search?.toLowerCase()),
          );
        }
        if (values.search === '') {
          this.filteredActedMitigations = [...filteredMitigations];
        }
      }
      this.filterActedConfiguration.totalRecords = this.filteredActedMitigations.length;
    });
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.subscriptions.forEach((s) => s.unsubscribe());
    const subscription = this.threatSrv.refreshObs.subscribe((rs) => {
      if (!!rs) {
        if (!!this.threat?.threat_id) {
          this.getThreatMitigation();
        }
      }
    });
    this.subscriptions.push(subscription);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.cleanupDialogs();
  }

  /**
   * Retrieve all mitigations and reset filters
   */
  getThreatMitigation() {
    this.isLoading = true;
    this.threatSrv
      .getThreatMitigation(this.threat.threat_id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          const mitigations = (res as any[]) || [];
          this.setMitigations(mitigations);
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  setMitigations(mitigations: any[]) {
    this.allMitigations = mitigations;

    this.filteredUnactedMitigations = this.allMitigations.filter((m) => !m.is_acted);
    this.filteredActedMitigations = this.allMitigations.filter((m) => !!m.is_acted);
    this.filterUnactedConfiguration.totalRecords = this.filteredUnactedMitigations.length;
    this.filterActedConfiguration.totalRecords = this.filteredActedMitigations.length;

    //Reset Filters
    this.cts?.forEach((ct) => ct.clearFilters());
  }

  /**
   * Open Mitigation Form for Creation or Edit
   * @param mitigation
   */
  openMitigationForm(mitigation?: any) {
    const dialog = this.dialogSrv.open(MitigationFormComponent, {
      header: `${!mitigation ? 'Create' : 'Update'} Custom Mitigation`,
      data: { threatId: this.threat?.threat_id || null, mitigation: mitigation },
      width: '800px',
      height: 'min-content',
      closable: false,
      modal: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        this.threatSrv.refresh$.next(true);
      }
    });
  }

  /**
   * Update Status or Edit existing mitigation
   * @param id
   * @param payload
   * @param successMessage
   */
  updateMitigation(id: any, payload: any, successMessage: string) {
    this.isLoading = true;
    this.threatSrv
      .updateMitigation(id, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage(successMessage);
          this.threatSrv.refresh$.next(true);
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  /**
   * Update mitigation status to Active
   * @param mitigation
   */
  updateMitigationIsActed(mitigation: any) {
    const payload = {
      is_acted: true,
      status: 'Active',
    };
    this.updateMitigation(mitigation.id, payload, `Mitigation ID: ${mitigation.id} has been initiated successfully`);
  }

  /**
   * Update mitigation status: Open, Active, Closed
   * @param event
   * @param mitigation
   */
  updateMitigationStatus(event: any, mitigation: any) {
    const payload = {
      is_acted: !(event.value === MITIGATION_STATUSES.OPEN),
      status: event.value,
    };
    this.updateMitigation(mitigation.id, payload, `Updated status mitigation ID: ${mitigation.id} successfully`);
  }

  /**
   * open dialog before deletion of mitigation
   * @param mitigation
   */
  openDeleteConfirmation(mitigation: any) {
    this.confirm({
      action: DELETE_LABEL,
      objectName: 'Mitigation',
      object: mitigation,
      objectFieldNameFunction: (object) => `Mitigation ID ${object.id} ${!!object.title ? ` : ${object.title}` : ''}`,
      acceptRequest: this.threatSrv.deleteMitigation(mitigation.id),
      next: () => {
        this.showSuccessMessage(`Deleted mitigation ${mitigation.title} successfully`);
        this.threatSrv.refresh$.next(true);
      },
      error: (err: any) => {
        this.showErrorMessage(err);
      },
    });
  }

  /**
   * get appropriate mitigations from filtered mitigations to display
   * @returns mitigations
   */
  get unactedMitigations(): any[] {
    return this.filteredUnactedMitigations.filter((mitigation) => !mitigation.is_acted);
  }

  get actedMitigations(): any[] {
    return this.filteredActedMitigations.filter((mitigation) => !!mitigation.is_acted);
  }
}
